/* ===========================================================
   Find Your Matcha — Landing Page JS
   Asset path: assets/matcha-lp.js

   Idempotent: safe to load multiple times. Initialization is
   guarded so re-running doesn't double-bind handlers.
   =========================================================== */

(function () {
  if (window.__matchaLpInit) return;
  window.__matchaLpInit = true;

  function init() {
    initPillScrollSpy();
    initCarouselDots();
    initVariantSelects();
    initAddToCartFeedback();
    initImageFallback();
    initFaqAccordion();
    initRecipeModal();
    initKlaviyoReviews();
    initScrollRestore();
    initFeaturedRecipeAnchor();
  }

  /* Featured recipe deep link (#apple-cider-matcha on the Supreme PDP). The jump to
     the anchor starts mid-load and content above keeps resizing, so it lands off
     target (2026-09-25: 192px past at 375px). Re-align to scroll-margin-top each
     drifted frame until 6s after load; stop on any scroll, tap, click, or key. */
  function initFeaturedRecipeAnchor() {
    var id;
    try { id = decodeURIComponent((window.location.hash || '').slice(1)); } catch (e) { return; }
    if (!id) return;
    var target = document.getElementById(id);
    if (!target || !target.classList.contains('recipe-feature')) return;

    var done = false;
    var events = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    function stop() {
      done = true;
      events.forEach(function (e) { window.removeEventListener(e, stop, true); });
    }
    events.forEach(function (e) { window.addEventListener(e, stop, { capture: true, passive: true }); });

    function drift() {
      var margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      return target.getBoundingClientRect().top - margin;
    }
    function align() {
      var html = document.documentElement;
      var prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
      html.style.scrollBehavior = prev;
    }
    function tick() {
      if (done) return;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      if (Math.abs(drift()) > 2 && !(drift() > 0 && window.scrollY >= max - 1)) align();
      window.requestAnimationFrame(tick);
    }
    window.requestAnimationFrame(tick);
    function stopLater() { setTimeout(stop, 6000); }
    if (document.readyState === 'complete') stopLater();
    else window.addEventListener('load', stopLater);
    setTimeout(stop, 20000);
  }

  /* "Jump to" row scroll-spy: marks the pill whose section sits in a band 30-40% down the
     viewport with .active and aria-current="location"; none is marked while the band is in
     the hero or in a section no pill points to (it used to keep the last one lit forever).
     Also publishes the row's height as --mlp-jump-height, which the desktop scroll-padding
     adds to the sticky header so a jump lands below both. Clicks stay native anchors. */
  function initPillScrollSpy() {
    var pills = document.querySelectorAll('.matcha-lp [data-pill]');
    if (!pills.length) return;

    var row = document.querySelector('.shopify-section-matcha-pill-nav');
    if (row) {
      var setHeight = function () {
        document.documentElement.style.setProperty('--mlp-jump-height', row.getBoundingClientRect().height + 'px');
      };
      setHeight();
      if (window.ResizeObserver) new ResizeObserver(setHeight).observe(row);
      else window.addEventListener('resize', setHeight);
    }

    var observed = [];
    var inBand = {};
    pills.forEach(function (pill) {
      var id = (pill.getAttribute('href') || '').replace(/^#/, '');
      var el = id && document.getElementById(id);
      if (el) observed.push(el);
    });
    if (!observed.length || !window.IntersectionObserver) return;

    function mark() {
      var current = null;
      for (var i = 0; i < observed.length; i++) {
        if (inBand[observed[i].id]) { current = observed[i].id; break; }
      }
      pills.forEach(function (p) {
        var on = current !== null && (p.getAttribute('href') || '') === '#' + current;
        p.classList.toggle('active', on);
        if (on) p.setAttribute('aria-current', 'location');
        else p.removeAttribute('aria-current');
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { inBand[entry.target.id] = entry.isIntersecting; });
      mark();
    }, { rootMargin: '-30% 0px -60% 0px', threshold: 0 });

    observed.forEach(function (el) { observer.observe(el); });
  }

  /* Carousel dots — one dot per scroll position.
     Position count = max(1, columns - visibleCount + 1). VisibleCount = 2.
     A two-row carousel (.product-carousel--rows2) holds 2 cards per column. */
  function initCarouselDots() {
    document.querySelectorAll('.matcha-lp [data-dots]').forEach(function (dotsContainer) {
      var carousel = dotsContainer.previousElementSibling;
      if (!carousel || !carousel.hasAttribute('data-carousel')) return;

      var cards = carousel.querySelectorAll('.product-card, .recipe-card');
      var perColumn = carousel.classList.contains('product-carousel--rows2') ? 2 : 1;
      var columns = Math.ceil(cards.length / perColumn);
      var visibleCount = 2;
      var positionCount = Math.max(1, columns - visibleCount + 1);
      if (positionCount < 2) return;

      for (var i = 0; i < positionCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'Go to position ' + (i + 1));
        (function (cardIndex) {
          dot.addEventListener('click', function () {
            cards[cardIndex * perColumn].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          });
        })(i);
        dotsContainer.appendChild(dot);
      }

      var scrollTimeout;
      carousel.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var cardStep = cards[0].getBoundingClientRect().width + 12;
          var activeIndex = Math.round(carousel.scrollLeft / cardStep);
          var clamped = Math.min(Math.max(activeIndex, 0), positionCount - 1);
          dotsContainer.querySelectorAll('.carousel-dot').forEach(function (dot, i) {
            dot.classList.toggle('active', i === clamped);
          });
        }, 60);
      }, { passive: true });
    });
  }

  /* Variant selects on multi-variant product cards.
     Updates the displayed price and CTA state when a variant changes. */
  function initVariantSelects() {
    document.querySelectorAll('.matcha-lp [data-variant-select], .dws [data-variant-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (!option) return;
        var card = select.closest('.product-card');
        if (!card) return;
        var priceEl = card.querySelector('[data-product-price]');
        if (priceEl && option.dataset.price) {
          priceEl.textContent = option.dataset.price;
        }
        var btn = card.querySelector('.product-cta');
        if (btn) {
          var available = option.dataset.available !== 'false';
          btn.disabled = !available;
          btn.textContent = available ? 'Add to Cart' : 'Sold Out';
        }
      });
    });
  }

  function initAddToCartFeedback() {
    document.querySelectorAll('.matcha-lp .product-form, .dws .product-form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('.product-cta');
        if (!btn || btn.disabled) return;

        var formData = new FormData(form);
        var original = btn.textContent;
        var originalLabel = btn.getAttribute('aria-label');
        function resetLabel() {
          if (originalLabel === null) btn.removeAttribute('aria-label');
          else btn.setAttribute('aria-label', originalLabel);
        }
        btn.setAttribute('aria-live', 'polite');
        btn.textContent = 'Adding…';
        btn.setAttribute('aria-label', 'Adding…');
        btn.disabled = true;

        formData.append('sections', 'mini-cart');
        fetch('/cart/add.js', { method: 'POST', body: formData })
          .then(function (r) {
            if (!r.ok) {
              return r.json().then(function (err) { throw new Error(err.description || err.message || 'Add to cart failed'); });
            }
            return r.json();
          })
          .then(function (added) {
            return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
              cart.sections = added.sections;
              return cart;
            });
          })
          .then(function (cart) {
            updateCartCounts(cart.item_count);
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
            var drawer = document.querySelector('cart-drawer');
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true, detail: { cart: cart, openMiniCart: !!drawer }
            }));
            if (!drawer) showCartToast('Added to cart');
            btn.textContent = 'Added ✓';
            btn.setAttribute('aria-label', originalLabel ? 'Added ✓: ' + originalLabel.replace(/^Add to Cart:\s*/, '') : 'Added ✓');
            btn.style.background = '#7a6224';
            btn.style.opacity = '1';
            setTimeout(function () {
              btn.textContent = original;
              resetLabel();
              btn.style.background = '';
              btn.style.opacity = '';
              btn.disabled = false;
            }, 1500);
          })
          .catch(function () {
            btn.textContent = 'Try again';
            btn.setAttribute('aria-label', 'Try again');
            setTimeout(function () {
              btn.textContent = original;
              resetLabel();
              btn.disabled = false;
            }, 1800);
          });
      });
    });
  }

  /* Update every visible cart-count badge we can find (themes use
     wildly different selectors — try the common ones). */
  function updateCartCounts(count) {
    var selectors = [
      '[data-cart-count]',
      '.cart-count',
      '.cart-count-bubble',
      '.cart-link__bubble-num',
      '.header__cart-count',
      '.site-cart-count',
      '#CartCount',
      '.cart .label',
      'a[href="/cart"] .label',
      'a[href="/cart"] [data-count]'
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.textContent = String(count);
        // Some themes hide the badge at 0 — show it when non-zero
        if (count > 0) el.style.display = '';
      });
    });
  }

  /* Theme-agnostic toast notification at top of viewport.
     Singleton — re-used across multiple adds. */
  function showCartToast(message) {
    var toast = document.getElementById('matcha-lp-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'matcha-lp-toast';
      toast.className = 'matcha-lp-toast';
      toast.innerHTML =
        '<span class="matcha-lp-toast-msg"></span>' +
        '<a href="/cart" class="matcha-lp-toast-link">View cart →</a>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.matcha-lp-toast-msg').textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._matchaLpToastTimeout);
    toast._matchaLpToastTimeout = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3500);
  }

  /* If a product image fails to load, swap in a styled gradient placeholder
     with the product name so the card stays readable. */
  function initImageFallback() {
    function handleError(img) {
      var parent = img.parentElement;
      if (!parent || parent.querySelector('.product-image-fallback')) return;
      img.style.display = 'none';
      var label = parent.getAttribute('data-fallback') || img.alt || 'Product';
      var fb = document.createElement('div');
      fb.className = 'product-image-fallback';
      fb.textContent = label;
      parent.appendChild(fb);
    }
    document.querySelectorAll('.matcha-lp .product-image img, .matcha-lp .recipe-image img').forEach(function (img) {
      if (img.complete && img.naturalWidth === 0) {
        handleError(img);
      } else {
        img.addEventListener('error', function () { handleError(img); });
      }
    });
  }

  /* FAQ accordion */
  function initFaqAccordion() {
    document.querySelectorAll('.matcha-lp .faq-q').forEach(function (q) {
      q.addEventListener('click', function () {
        var item = q.closest('.faq-item');
        if (item) item.classList.toggle('is-open');
      });
    });
  }

  /* Recipe modal — opens on card click, closes via overlay/X/Escape,
     prints via window.print. Hash deep-linking: #recipe-iced-matcha
     auto-opens that recipe. Email-friendly. */
  function initRecipeModal() {
    var modal = document.getElementById('recipeModal');
    var modalContent = document.getElementById('recipeModalContent');
    if (!modal || !modalContent) return;

    function open(recipeId) {
      var source = document.getElementById('recipe-' + recipeId);
      if (!source) return;
      modalContent.innerHTML = source.innerHTML;
      modalContent.scrollTop = 0;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('matcha-modal-open');
      var newHash = '#recipe-' + recipeId;
      if (window.location.hash !== newHash) {
        history.replaceState(null, '', newHash);
      }
    }

    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('matcha-modal-open');
      if (window.location.hash.indexOf('#recipe-') === 0) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    function openFromHash() {
      var match = window.location.hash.match(/^#recipe-(.+)$/);
      if (match && document.getElementById('recipe-' + match[1])) {
        setTimeout(function () { open(match[1]); }, 150);
      }
    }

    window.addEventListener('load', openFromHash);
    window.addEventListener('hashchange', openFromHash);

    document.querySelectorAll('.matcha-lp .recipe-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault();
        var recipeId = card.getAttribute('data-recipe');
        if (recipeId) open(recipeId);
      });
    });

    document.querySelectorAll('.matcha-lp [data-recipe-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    var printBtn = modal.querySelector('.recipe-modal-print');
    if (printBtn) {
      printBtn.addEventListener('click', function () { window.print(); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  /* Klaviyo Reviews — fetches 5-star reviews for the products selected
     in the reviews section blocks and swaps placeholder quotes for the
     real thing. Silent failure: if the endpoint is blank, unreachable,
     or returns no reviews for a given product, the manual fallback
     copy entered in the block remains. */
  function initKlaviyoReviews() {
    var container = document.querySelector('.matcha-lp .reviews[data-reviews-endpoint]');
    if (!container) return;
    var endpoint = container.getAttribute('data-reviews-endpoint');
    var productIds = container.getAttribute('data-product-ids');
    if (!endpoint || !productIds) return;

    var sep = endpoint.indexOf('?') === -1 ? '?' : '&';
    var url = endpoint + sep + 'product_ids=' + encodeURIComponent(productIds);

    fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.reviews || !data.reviews.length) return;
        // First review per product wins
        var byProduct = {};
        data.reviews.forEach(function (rev) {
          var pid = String(rev.product_id);
          if (!byProduct[pid]) byProduct[pid] = rev;
        });
        container.querySelectorAll('.review-card[data-product-id]').forEach(function (card) {
          // Skip cards admin has pinned with manual content
          if (card.getAttribute('data-pinned') === 'true') return;
          var pid = card.getAttribute('data-product-id');
          var rev = byProduct[pid];
          if (!rev) return;
          var quoteEl = card.querySelector('.review-quote');
          var authorEl = card.querySelector('.review-author');
          if (quoteEl && rev.content) {
            quoteEl.textContent = rev.content;
          }
          if (authorEl && rev.author_name) {
            authorEl.textContent = '— ' + rev.author_name;
          }
        });
      })
      .catch(function () { /* silent — fallback content stays */ });
  }

  /* Scroll restore: when a visitor leaves the LP via a product card link (which
     carries ?from=find-your-matcha), record the LP scroll position so we can
     return them to the exact spot on the way back. This asset ALSO loads on the
     matcha PDPs (they render the matcha-faq section), so gate to the LP path —
     otherwise we'd wrongly scroll a PDP to the stored LP position. */
  function initScrollRestore() {
    if (window.location.pathname.indexOf('find-your-matcha') === -1) return;

    var KEY = 'matcha-lp:return';
    var MAX_AGE = 20 * 60 * 1000; // ignore anything older than 20 min

    // Capture the departure position on any pass-back card link (capture phase,
    // so it fires even if a handler stops propagation). sessionStorage writes
    // synchronously, so it lands before the navigation.
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="from=find-your-matcha"]') : null;
      if (!a) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({
          y: window.scrollY || window.pageYOffset || 0,
          t: Date.now()
        }));
      } catch (err) { /* private mode / quota — falls back to the #anchor scroll */ }
    }, true);

    // A bfcache back-navigation restores scroll natively and never re-fires
    // DOMContentLoaded — clear the key so a later fresh visit can't reuse it.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { try { sessionStorage.removeItem(KEY); } catch (err) {} }
    });

    // Restore on (re)entry to the LP, once. The return link's #section anchor
    // already lands near the right row; this overrides it with the exact Y.
    var raw;
    try { raw = sessionStorage.getItem(KEY); } catch (err) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(KEY); } catch (err) {}

    var data;
    try { data = JSON.parse(raw); } catch (err) { return; }
    if (!data || typeof data.y !== 'number') return;
    if (Date.now() - (data.t || 0) > MAX_AGE) return;

    var y = data.y;
    // Defer past the browser's native #anchor jump and late layout; the second
    // pass catches shifts once lazy media above the fold settles.
    window.requestAnimationFrame(function () {
      window.scrollTo(0, y);
      setTimeout(function () { window.scrollTo(0, y); }, 60);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
